import React, { FC, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { CloseDialogFunc } from '../../hooks/useCloseDialog';
import Button from '@mui/material/Button';
import { IServerSidePaginatedFetcherParams } from '../../contexts/Fetcher';
import { AutocompleteServersideController } from '../InputControllers/AutocompleteController';
import { useForm } from 'react-hook-form';
import { AxiosRequestConfig } from 'axios';
import { useSearchableDropdown } from '../../hooks/useSearchableDropdown';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { IBasicOrganization } from '../../resources/models/IOrganization';
import { apiLicense } from '../../resources/api/apiLicense';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { ILicense } from '../../models/ILicense';
import { useTranslation } from 'react-i18next';
import { TypographyBody1 } from '../Typography/Typography';
import { CellContentTooltip } from '../CellContentTooltip/CellContentTooltip';
import { WarningAlert } from '../Alert/WarningAlert';

interface IProps {
  isOpen: boolean;
  closeDialog: CloseDialogFunc;
  onRefresh: () => Promise<any>;
  organization: IBasicOrganization;
  license: ILicense;
}

const { useListOrganizationUsersSilent } = apiOrganization;
const { useAssignLicense } = apiLicense;

export const AssignLicenseModal: FC<IProps> = ({ isOpen, closeDialog, onRefresh, organization, license }) => {
  const { t } = useTranslation();
  const { executeWithConfig } = useListOrganizationUsersSilent(organization.name);

  const form = useForm();
  const user = form.watch('user');

  const { execute: assign } = useAssignLicense(organization, user?.id);

  const getUsers = useCallback(
    async (params: IServerSidePaginatedFetcherParams, config: AxiosRequestConfig) => {
      const result = await executeWithConfig(params, config);
      return result.payload.data;
    },
    [executeWithConfig]
  );

  const { data, onSearch, isLoading } = useSearchableDropdown(getUsers, isOpen);

  const items = data;

  useEffect(() => {
    form.reset({ user: null });
  }, []);
  const handleError = useApiErrorHandler();

  const onSubmit = useCallback(async () => {
    try {
      await assign({
        packages: {
          packageId: license.packageId,
          amount: 1,
          customerProductId: license.customerProductId,
        },
      });
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [assign, license, onRefresh]);

  return (
    <Dialog open={isOpen} onClose={closeDialog}>
      <DialogTitle>{t('assignLicense.title')}</DialogTitle>

      <DialogContent>
        <TypographyBody1>{t('assignLicense.message', { license: license.name })}</TypographyBody1>
        <AutocompleteServersideController
          control={form.control}
          errors={form.formState.errors}
          items={items}
          label={t('assignLicense.user')}
          renderOption={(props, user) => {
            return (
              <li {...props} style={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
                <CellContentTooltip noWrap>
                  {user?.first_name} {user?.last_name}
                </CellContentTooltip>
                <Box
                  sx={{ ml: 'auto', pl: 1, color: 'text.disabled', flexShrink: 0, maxWidth: '50%', overflow: 'hidden' }}
                >
                  <CellContentTooltip noWrap>{user?.email}</CellContentTooltip>
                </Box>
              </li>
            );
          }}
          getOptionLabel={(user) =>
            user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.email ?? ''
          }
          name={'user'}
          onSearch={onSearch}
          isLoading={isLoading}
        />
        {user && !user.verified && (
          <Box sx={{ mt: 2 }}>
            <WarningAlert>{t('assignLicense.notAcceptedInvitation')}</WarningAlert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={closeDialog}>
          {t('common.dialog.close')}
        </Button>
        <Button variant={'contained'} color={'primary'} onClick={form.handleSubmit(onSubmit)} disabled={!user}>
          {t('assignLicense.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
